/**
 * RangePicker — 7/30/90-day presets + custom date range (inclusive,
 * site-local dates).
 *
 * The custom inputs are locally controlled while the user is editing and
 * commit on BLUR or Enter, never on every `change` event. A `<input
 * type="date">` fires `change` for each keystroke that leaves the field
 * momentarily complete, so typing a year emits intermediate whole dates
 * (0002-…, 0020-…, 0202-…, 2026-…). Committing those would fire a fetch per
 * keystroke, churn the range LRU, and flash the backend's 400-day range cap
 * as an error before the user finished typing.
 */

import { useEffect, useState } from 'react';
import SegmentedControl from '../../ui/SegmentedControl';
import { fieldCls } from '../../ui';
import { RangeKey } from '../types';

interface RangePickerProps {
  range: RangeKey;
  from: string;
  to: string;
  onRange: (key: RangeKey) => void;
  onCustom: (from: string, to: string) => void;
}

export function RangePicker({ range, from, to, onRange, onCustom }: RangePickerProps) {
  const [draft, setDraft] = useState({ from, to });

  // Re-sync whenever the committed range changes underneath us: switching to
  // "Custom" seeds the inputs from the preset that was showing, and a preset
  // rolling forward at site-local midnight must not leave a stale draft.
  useEffect(() => {
    setDraft({ from, to });
  }, [from, to]);

  const commitFrom = () => {
    if (draft.from && draft.from !== from) onCustom(draft.from, to);
    else setDraft((d) => ({ ...d, from })); // cleared or unchanged — snap back
  };

  const commitTo = () => {
    if (draft.to && draft.to !== to) onCustom(from, draft.to);
    else setDraft((d) => ({ ...d, to }));
  };

  const commitOnEnter = (e: React.KeyboardEvent<HTMLInputElement>, commit: () => void) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <SegmentedControl
        label="Date range"
        value={range}
        onChange={(v) => onRange(v as RangeKey)}
        options={[
          { value: '7d', label: '7d', title: 'Last 7 days' },
          { value: '30d', label: '30d', title: 'Last 30 days' },
          { value: '90d', label: '90d', title: 'Last 90 days' },
          { value: 'custom', label: 'Custom' },
        ]}
      />
      {range === 'custom' && (
        <div className="flex items-center gap-1.5">
          <input
            type="date"
            value={draft.from}
            max={to}
            onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
            onBlur={commitFrom}
            onKeyDown={(e) => commitOnEnter(e, commitFrom)}
            className={fieldCls}
            aria-label="From date"
          />
          <span className="text-sm text-gray-500 dark:text-gray-400">to</span>
          <input
            type="date"
            value={draft.to}
            min={from}
            onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
            onBlur={commitTo}
            onKeyDown={(e) => commitOnEnter(e, commitTo)}
            className={fieldCls}
            aria-label="To date"
          />
        </div>
      )}
    </div>
  );
}

export default RangePicker;
